import React from 'react';
import _ from 'lodash';
import LivePage from '../LivePage';
import LegoAdminPageContext from '../legoAdminPageContext';
import SchemaEditor, { getCleanSchema } from './spec-schema-editor/SpecSchemaEditor';
import submenuCerokm from '../menus/submenu-cerokm';
import BadgeId from '../../components/common/BadgeId';
import { UserAvatar } from '../../components/common/UserAvatar';
import TextDiff from '../../components/test-sets/TextDiff';
import { IconButton } from '../../components/common/IconButton';
import { SchemaChangeOpsList } from './components/SchemaChangeOpsList';
import SaveSchemaChangePreview from './components/SaveSchemaChangePreview';
import SchemaFixesExplorer from './components/SchemaFixesExplorer';
import { fromNow } from '../../components/common/TimeUtils';
import SchemaVersionBadge from './components/SchemaVersionBadge';
import LiveScriptSummaryBar from '../../components/script-runs/LiveScriptSummaryBar';
import FragmentsPreview from './components/FragmentsPreview';
import FragmentsUsingSpec from './components/FragmentsUsingSpec';

class CeroKmSchemaManager extends LivePage {
  constructor(props) {
    super(props);
    // this.svcSource = this.service('services/cerokm/source');
    // this.svcFragments = this.service('services/cerokm/sourcefragment');
    this.svcSchemas = this.service('services/cerokm/schema');
    this.submenu = submenuCerokm;
    this.fullScreen = true;

    this.editorRef = React.createRef();

    this.state.temporalFixesState = {};
    this.state.appliedFixes = []; 

    this.handleSchemaUpdate = this.handleSchemaUpdate.bind(this);
    this.handleSchemaCreated = this.handleSchemaCreated.bind(this);
    this.handleSchemaRemoved = this.handleSchemaRemoved.bind(this);
  }

  componentDidMount() {
    this.svcSchemas.on('updated', this.handleSchemaUpdate);
    this.svcSchemas.on('created', this.handleSchemaCreated);
    this.svcSchemas.on('removed', this.handleSchemaRemoved);
    
    this.runAsync(this.refreshSchema());
  }

  async refreshSchema() {
    let res = await this.svcSchemas.find({ query: { $sort: { createdAt: -1 }, $limit: 1 } });
    if (this.hasSchemaChanges()) {
      if (res[0]?.version > this.state.originalSchema.version) {
        this.openSchemaUpdate(res[0]);
      }
    } else {
      this.setState({ originalSchema: res[0] || { schema: { type: 'object', properties: {} } } });
    }
  }

  handleSchemaUpdate(obj, ... args) {
    console.log('handleObjectUpdate', obj, args);
  }

  handleSchemaRemoved(obj, ... args) {
    console.log('handleSchemaRemoved', obj, args);
  }

  handleSchemaCreated(doc) {
    const expecting = this.state.expectingNewDoc;
    if(expecting) {
      if(expecting.version === doc.version && _.isEqual(expecting.schema, doc.schema)) {
        console.log(`%c${new Date().toISOString()} IGNORE SAFELY CREATED SCHEMA, ITS ME`, 'color: green', doc);
        this.setState({ expectingNewDoc: null });
      } else {
        alert('Unexpected: saved schema, but a different schema change occurred');
        console.log(`%c${new Date().toISOString()} UNEXPECTED`, 'color: red');
      }
    } else if(doc.version > this.state.originalSchema.version) {
      if(!this.hasSchemaChanges()) {
        console.log(`%c${new Date().toISOString()} NEW SCHEMA VERSION ${doc.version} IS HIGHER THAN THE CURRENT ${this.state.originalSchema.version}, UPDATING ORIGINAL`, 'color: orange');

        this.showNotification({
          level: 'info', 
          title: `Schema update`, 
          body: <div>Schema updated from <SchemaVersionBadge version={this.state.originalSchema.version}/> to <SchemaVersionBadge version={doc.version}/> by <UserAvatar user={doc.createdBy}/> only {fromNow(doc.createdAt)}</div>,
        });

        this.discardChanges(doc);
      } else {
        this.openSchemaUpdate(doc);
      }
    } else {
      alert('Unexpected: a schema with a lower version than the original was created, maybe out of order updates occurred ¿?');
    }
  }

  openSchemaUpdate(doc) {
    this.openModal(<div>
      <div>
        <h6>Schema was updated from <SchemaVersionBadge version={this.state.originalSchema.version}/> to <SchemaVersionBadge version={doc.version}/>, rebase your changes?</h6>
        <div className={'bg-light rounded text-secondary p-2 small overflow-auto'} style={{maxHeight: '500px'}}>
          <TextDiff type={'json'} inputB={doc.schema} ellipsis={250} inputA={this.state.originalSchema.schema}/>
        </div>
      </div>
      <div>
        <button className={'btn btn-primary m-1'} onClick={() => {
          let operations = [... this.state.operations];                
          this.setState({ originalSchema: doc, changedSchema: null, operations: [] });
          // Wait until the editor updates its internal state
          setTimeout(() => {
            try {
              this.editorRef.current.applyOperations(operations);
            } catch(e) {
              this.prompt("There was a problem applying the operations, copy the json below to not lose your changes", JSON.stringify(operations), {multiline: true});
            } finally {
              this.closeModal();
            }
          }, 1);
        }}>
          Rebase changes
        </button>

        <button className={'btn btn-sm btn-secondary m-1'} onClick={() => {
          this.setState({ outdatedSchema: true });
          this.closeModal();
        }}>Ignore, I'll discard my changes later</button>
      </div>
    </div>, {title: 'Schema updated'});
  }

  async addOperationsFromJson() {
    const operationsJSON = await this.prompt('Paste the operations you want to add:');
    if (operationsJSON) {
      try {
        let operations = JSON.parse(operationsJSON);
        try {
          this.editorRef.current.applyOperations(operations);
        } catch (e) {
          alert("There was a problem applying the operations:\n\n" + e.toString());
        } finally {
          this.closeModal();
        }
      } catch (e) {
        alert('Invalid JSON');
      }
    }
  }
  
  componentWillUnmount() {
    this.svcSchemas.removeListener('updated', this.handleSchemaUpdate);
    this.svcSchemas.removeListener('created', this.handleSchemaCreated);
    this.svcSchemas.removeListener('removed', this.handleSchemaRemoved);
  }

  saveChanges() {
    if(this.hasSchemaChanges()) {
      this.openModal(
        <SaveSchemaChangePreview 
          operations={this.state.operations} 
          changedSchema={this.state.changedSchema}
          originalSchema={this.state.originalSchema}
          fixesState={this.state.temporalFixesState}
        appliedFixes={this.state.appliedFixes}
        onSaveFired={(newDoc) => {
          console.log('Expecting save side effects')
          this.setState({ expectingNewDoc: newDoc });
        }}
        onSavedChanges={(savedNewDoc) => {
          console.log(new Date().toISOString() + " ONLY NOW UPDATING ORIGINAL SCHEMA")
          this.setState({ originalSchema: savedNewDoc, operations: [], appliedFixes: [], temporalFixesState: {}, forceRefresh: new Date().valueOf() });
          this.closeModal();
        }}
      />, 
        {title: 'Save changes - Schema change impact'}
      );
    } else {
      this.runAsync(async () => {
        let toReject = _.filter(_.toPairs(this.state.temporalFixesState), ([id, state]) => state === 'reject');
        if(toReject.length) {
          await this.service('services/cerokm/schemafix').update({query: { _id: {$in: toReject.map(([id,state]) => id)}}}, {$set: {state: 'rejected'}});
          this.setState({ appliedFixes: [], operations: [], temporalFixesState: {}, forceRefresh: new Date().valueOf() });
        }
      }, 'Saving fixes decisions...');
    }
  }

  hasSchemaChanges() {
    return this.state.changedSchema && !_.isEqual(this.state.originalSchema.schema, getCleanSchema(this.state.changedSchema));
  }

  discardChanges(optionalResetSchema = null) {
    this.setState({ 
      originalSchema: _.cloneDeep(optionalResetSchema || this.state.originalSchema), 
      temporalFixesState: {},
      operations: [],
      appliedFixes: [] 
    });
  }


  openModalDiffChanges() {
    let inputB = JSON.stringify(getCleanSchema(this.state.changedSchema), null, 2);
    let inputA = JSON.stringify(this.state.originalSchema.schema, null, 2);

    this.modalActionsBus.emit('open', <div>
      <div className={'p-2 bg-light monospace small break-word-all'}>
        <TextDiff type={'json'} inputB={inputB} ellipsis={250} inputA={inputA}/>
      </div>
    </div>, false);
  }
  
  applyFix(ops, fix) {
    try {
      this.editorRef.current.applyOperations(ops);
    } catch(e) {
      return alert(e.toString());
    }
    this.editorRef.current.expandOperationPaths();

    this.setState(prev => ({appliedFixes: [...prev.appliedFixes, fix]}));
    this.setState(prevState => ({ temporalFixesState: { ...prevState.temporalFixesState, [fix._id]: 'accept' } }));  
  }

  rejectFix(fix) {
    this.setState(prevState => ({ temporalFixesState: { ...prevState.temporalFixesState, [fix._id]: 'reject' } }));
  }

  onPathExplore(path, value) {
    console.log('onPathExplore', path, value);
    this.openModal(<FragmentsUsingSpec path={path}  />, { title: 'Factoids using spec ', fullScreen: true });
  }

  renderPageBody() {
    let editor = <span className={'text-secondary'}>Loading...</span>;

    const { changedSchema, originalSchema, temporalFixesState, forceRefresh, operations } = this.state;

    if (originalSchema) {
      const { schema, createdBy, createdAt, updatedAt } = originalSchema;

      const hasSchemaChanges = this.hasSchemaChanges();
      const changedFixes = !_.isEmpty(temporalFixesState);
      const unsavedChanges = hasSchemaChanges || changedFixes;

      editor = <div className={'grid-schema-editor'}>

        <div className={`d-flex py-2 px-3 align-items-md-center border-bottom ${unsavedChanges ? 'alert-warning' : 'bg-light'}`}
             style={{ minHeight: '50px', gap: '20px' }}>
          <IconButton icon={'refresh'} level={'primary'} description={'Refresh to latest schema'} onClick={() => this.runAsync(this.refreshSchema())}/>

          <div>

            <div className={'text-secondary zoom-90 small align-middle'} title={createdAt}>
              {createdBy ? <UserAvatar user={createdBy}/> : null} &nbsp;
              {createdAt ?
                <span className={'align-middle'}>Created {fromNow(updatedAt || createdAt)}</span> : null}
            </div>

            <BadgeId id={originalSchema._id}/>
          </div>

          <SchemaVersionBadge version={originalSchema.version}/>

          {unsavedChanges && <div className={'alert alert-warning mb-0 py-1'}>
            { hasSchemaChanges ? 'Unsaved changes' : 'Unsaved fixes decisions' }

            <button className={'btn btn-sm btn-primary ml-2'} onClick={() => this.discardChanges()}>
              Discard
            </button>

            <button className={'btn btn-sm btn-success ml-2 mr-2'} onClick={() => this.saveChanges()}>
              { hasSchemaChanges ? 'Save as new version' : 'Save fixes decisions' }
            </button>

            { hasSchemaChanges && <IconButton icon={'compare'} level={'primary'} description={'See diff of changes'}
                        onClick={this.openModalDiffChanges.bind(this)}>See diff</IconButton>
            }
          </div>}

          {!unsavedChanges && <div className={'alert alert-success mb-0 py-1'}>No changes</div>}

          <LiveScriptSummaryBar name={'ParseFragments'}/>
        </div>


      <div className={''}>
        <SchemaEditor key={originalSchema} value={schema} ref={this.editorRef} onPathExplore={this.onPathExplore.bind(this)} onChange={(v, operations) => this.setState({ changedSchema: v, operations })}/>
      </div>


      <div className={'bg-light p-2'}>
        <h6>
          Find unmodeled specs and fix the schema
          <IconButton icon={'refresh'} level={'primary'} description={'Refresh'} onClick={() => this.setState({ forceRefresh: new Date().valueOf() })}/>
        </h6>

        <SchemaFixesExplorer 
          onApplyFix={this.applyFix.bind(this)} 
          onRejectFix={this.rejectFix.bind(this)}
          forceRefresh={forceRefresh}
          temporalFixesState={temporalFixesState}
        />
        <pre className={'small zoom-75'}>{JSON.stringify(temporalFixesState, null, 2)}</pre>

        <hr/>
        <SchemaChangeOpsList ops={operations}/>
        <IconButton icon={'add'} level={'primary'} description={'Add operations from JSON'} onClick={this.addOperationsFromJson.bind(this)}>Add operations from JSON</IconButton>
        <hr/>
        <pre className={'small zoom-75'}>{JSON.stringify(operations, null, 2)}</pre>
      </div>
    </div>;
    }

    return editor;
  }
}

CeroKmSchemaManager.contextType = LegoAdminPageContext;

// For development
function FuncComponent(props) {
  return <CeroKmSchemaManager {...props}/>;
}

export default FuncComponent;
